import { DefaultRootState } from 'react-redux';

import fetcher from 'api/fetcher';
import axios, { AxiosError } from 'axios';
import { formatCost } from 'utils/clickme';
import { NON_BREAKING_SPACE } from 'utils/constants/common';
import { MagritteNotificationName } from 'utils/constants/notification';

import promoCodeActivateFormSubmit from '@hh.ru/analytics-js-events/build/clickme/employer/balance_and_bonuses/promo_code_activate_form_submit';

import { translate } from 'hooks/translation/useTrls';

import {
    addMagritteCustomNotification,
    addMagritteNotification,
    MagritteNotificationType,
} from 'store/models/notification';
import { AppDispatch } from 'store/utils';

enum PromoCodeActivationStatus {
    Success = 'SUCCESS',
    AlreadyUsed = 'ALREADY_USED',
    Failed = 'FAILED',
}

interface PromoCodeActivationResponse {
    status: PromoCodeActivationStatus;
    bonus: {
        sum: number;
    };
}

type ActivatePromoCode = (
    promoCode: string
) => (dispatch: AppDispatch, getState: () => DefaultRootState) => Promise<PromoCodeActivationResponse | void>;

export const activatePromoCode: ActivatePromoCode = (promoCode) => {
    return async (dispatch, getState) => {
        const trls = getState().translations.trls;
        return fetcher
            .put<unknown, PromoCodeActivationResponse>(`/api/v1/promos/code/${promoCode}/activate`)
            .then((response) => {
                const { status, bonus } = response;

                promoCodeActivateFormSubmit({
                    inputValue: promoCode,
                    status,
                });

                if (status === PromoCodeActivationStatus.Success) {
                    const bonusText = bonus.sum
                        ? translate(trls, 'BalanceAndBonuses.PromoCodeCard.success.withBonus', [
                              formatCost(bonus.sum, NON_BREAKING_SPACE),
                          ])
                        : translate(trls, 'BalanceAndBonuses.PromoCodeCard.success.withoutBonus');
                    dispatch(
                        addMagritteCustomNotification({
                            type: MagritteNotificationType.Success,
                            text: bonusText,
                        })
                    );
                    return response;
                }

                if (status === PromoCodeActivationStatus.AlreadyUsed) {
                    dispatch(addMagritteNotification(MagritteNotificationName.PromoCodeAlreadyUsed));
                    throw new Error('Промокод уже применён');
                }

                if (status === PromoCodeActivationStatus.Failed) {
                    dispatch(addMagritteNotification(MagritteNotificationName.PromoCodeFailed));
                    throw new Error('Неправильный промокод');
                }

                return response;
            })
            .catch((err: AxiosError<Error>) => {
                if (axios.isAxiosError(err) && err.response?.status !== 200) {
                    dispatch(addMagritteNotification(MagritteNotificationName.PromoCodeActivationError));
                    promoCodeActivateFormSubmit({
                        inputValue: promoCode,
                        errors: JSON.stringify(err),
                        status: 'FAILED',
                    });
                }

                throw err;
            });
    };
};
